/* @HEADER
 * @version 1.0.27 | 2026-08-19
 * @purpose Keep the screen a person was looking at on screen across the reload an update performs, instead of replacing their application with a loading placeholder (ZVX-DEV-4244).
 * @sidecar shellSnapshot.ts.header.md
 * @layer app | @hex _none_ | @ctx _none_
 * @public true
 * @edit careful
 */

// SpecRefs: ZVX-DEV-4244, ZVX-DEV-4302, ZVX-DEV-4220, ZVX-DEV-4026

/**
 * ## What this is for
 *
 * The operator, twice: "никогда не отсутствующий UI" — never a screen without
 * the application on it. ZVX-DEV-4220 closed the case where the reload could
 * not be served at all. This closes the ordinary one: a reload that WORKS still
 * tears the page down, and for the second or two the new bundle needs to parse,
 * `index.html` showed "Загружаем Zvenix…" where the person's work had been.
 *
 * So the last rendered screen is written down just before the update reloads,
 * and `index.html` paints it immediately on the way back up. React then mounts
 * over it. The person sees their own interface the whole way through, and the
 * only thing that changes is that it becomes interactive again.
 *
 * ## Why it is one-shot, and why that matters
 *
 * The snapshot is written ONLY by the update-apply path and is CONSUMED (read
 * and deleted) by the very next boot. It is not a warm-start cache and must
 * never become one:
 *
 *   - a stored copy of a rendered screen is a copy of the person's data, and
 *     the shorter it exists the better. Seconds, by construction;
 *   - a ghost of an authenticated screen must never greet someone who has since
 *     signed out. One-shot plus {@link SNAPSHOT_MAX_AGE_MS} makes that
 *     impossible rather than unlikely — a reload that does not happen within
 *     two minutes of the click gets nothing;
 *   - and it can never mask a real failure, because it is gone by the second
 *     boot: if the new build cannot start, index.html clears the painted
 *     picture once its silent previous-build rescue has had its chance and
 *     failed (ZVX-DEV-4273, ZVX-DEV-4431) — an empty page, not a lingering
 *     ghost of a build that is no longer running.
 *
 * The painted copy is inert (`pointer-events: none`, `aria-hidden`), because it
 * is a picture of an application, not an application. Clicking it must do
 * nothing rather than something wrong.
 */

/** localStorage key. Versioned: a future shape change must not be read as this one. */
export const SHELL_SNAPSHOT_KEY = 'zvenix_shell_snapshot_v1';

/** A snapshot older than this is not painted — it belongs to a reload that never came. */
export const SNAPSHOT_MAX_AGE_MS = 120_000;

/**
 * Above this, the snapshot is dropped rather than stored. A very large screen
 * is exactly the one whose `innerHTML` costs more to serialize and re-parse
 * than the placeholder it replaces, and localStorage is a small, shared budget
 * — overflowing it would break unrelated features to prettify a two-second gap.
 */
export const SNAPSHOT_MAX_BYTES = 400_000;

/** The minimal `Storage` surface used here — so a test can pass a plain object. */
export interface StorageLike {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
}

export interface ShellSnapshot {
  html: string;
  at: number;
  /**
   * ZVX-DEV-4302 -- the build that WROTE it. The snapshot is written by the
   * version being left behind, so "why was there no restored screen" is
   * always a question about that build, not about the one that boots. Without
   * this the answer was two people comparing impressions; a measurement on
   * staging showed the placeholder for ~80ms and the restored screen from
   * ~83ms to ~500ms, while the operator reported seconds of blank — and
   * nothing on either side could say which build had run the capture.
   */
  from?: string;
}

/**
 * Serialize a snapshot, or `null` when there is nothing worth keeping: an
 * empty screen (nothing to show) or one over {@link SNAPSHOT_MAX_BYTES}.
 */
export function buildShellSnapshot(
  html: string | null | undefined,
  now: number,
  from?: string | null,
): ShellSnapshot | null {
  const markup = typeof html === 'string' ? html.trim() : '';
  if (markup.length === 0) return null;
  if (markup.length > SNAPSHOT_MAX_BYTES) return null;
  const writer = typeof from === 'string' && from.length > 0 ? from : '';
  return { html: markup, at: now, ...(writer ? { from: writer } : {}) };
}

/**
 * Store a snapshot. Never throws: a full or unavailable localStorage (private
 * mode, quota) must not break the update the person actually asked for — the
 * worst case is the placeholder they see today.
 */
export function saveShellSnapshot(storage: StorageLike | null | undefined, snapshot: ShellSnapshot | null): boolean {
  if (!storage || !snapshot) return false;
  try {
    storage.setItem(SHELL_SNAPSHOT_KEY, JSON.stringify(snapshot));
    return true;
  } catch {
    return false;
  }
}

/**
 * Read the snapshot and REMOVE it in the same step — the removal happens even
 * when the value is stale or unreadable, so a snapshot can never be shown
 * twice and can never outlive the reload it was written for.
 */
export function readAndConsumeShellSnapshot(
  storage: StorageLike | null | undefined,
  now: number,
  maxAgeMs: number = SNAPSHOT_MAX_AGE_MS,
): string | null {
  if (!storage) return null;
  let raw: string | null = null;
  try {
    raw = storage.getItem(SHELL_SNAPSHOT_KEY);
    storage.removeItem(SHELL_SNAPSHOT_KEY);
  } catch {
    return null;
  }
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw) as Partial<ShellSnapshot>;
    if (typeof parsed?.html !== 'string' || parsed.html.length === 0) return null;
    if (typeof parsed?.at !== 'number' || !Number.isFinite(parsed.at)) return null;
    const age = now - parsed.at;
    if (age < 0 || age > maxAgeMs) return null;
    return parsed.html;
  } catch {
    return null;
  }
}

/**
 * Capture the currently rendered application and store it, for the boot that
 * follows the update reload. Called from the apply flow only.
 */
export function captureShellForUpdate(
  doc: { getElementById(id: string): { innerHTML: string } | null } | null | undefined,
  storage: StorageLike | null | undefined,
  now: number,
  from?: string | null,
): boolean {
  if (!doc) return false;
  const root = doc.getElementById('root');
  if (!root) return false;
  return saveShellSnapshot(storage, buildShellSnapshot(root.innerHTML, now, from));
}
